import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { InsightsLabV2Report } from 'api/insightsLabV2'
import ChevronRight from 'components/Icons/ChevronRight'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('insights_lab_v2.picker')

type Props = {
  report: InsightsLabV2Report
  onOpen: (report: InsightsLabV2Report) => void
}

// A single report row in the browser's report list.
const ReportButton = ({ report, onOpen }: Props): JSX.Element => {
  const [css] = useStyletron()
  // Ties the visible description to the button's accessible description
  // (see aria-describedby below) — unique per report so multiple rows in
  // the same list don't collide.
  const descriptionId = `insights-lab-v2-report-description-${report.id}`

  return (
    <button
      type="button"
      // Removing the browser's default button border/focus ring means a
      // visible focus indicator must be restored for keyboard users
      // (WCAG 2.4.7 Focus Visible).
      className={`w-100 text-left p-m ${css({
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: '16px',
        // Set explicitly (rather than relying on Bootstrap's `border-0`,
        // which compiles with `!important` and always wins over the
        // longhand borderBottom below) so both the reset and the divider
        // are controlled by this one un-overridden source of truth.
        // Fully longhand per side (width/style/color), matching this repo's
        // established convention for a styletron border reset (see
        // `solidBorder` in src/scenes/LiveUpdates/components/styleHelpers.ts)
        // rather than mixing the `border` shorthand with `borderBottom` —
        // atomic CSS class insertion order, not CSS cascade rules, decides
        // which one wins, so that combination is fragile (see
        // context/knowledge/styletron-css-cascade.md).
        borderTopWidth: '0',
        borderTopStyle: 'none',
        borderTopColor: 'transparent',
        borderRightWidth: '0',
        borderRightStyle: 'none',
        borderRightColor: 'transparent',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: palette.grey20,
        borderLeftWidth: '0',
        borderLeftStyle: 'none',
        borderLeftColor: 'transparent',
        // Set explicitly (rather than relying on Bootstrap's bg-transparent,
        // which compiles with `!important` and always wins over :hover) so
        // both states are controlled by this one un-overridden source of truth.
        backgroundColor: 'transparent',
        ':hover': {
          backgroundColor: palette.grey10,
        },
        ':focus-visible': {
          outline: `2px solid ${palette.belizeHole}`,
          outlineOffset: '2px',
        },
      })}`}
      // Screen readers in list/buttons view announce only the accessible name,
      // so fold the category into it; the category is only visual otherwise,
      // and is no longer printed as its own line now that the description
      // occupies that spot.
      aria-label={
        report.categoryName
          ? t('report_label', {
              title: report.title,
              category: report.categoryName,
            })
          : report.title
      }
      // aria-label fully replaces the accessible name, so the visible
      // description text would otherwise never reach assistive tech —
      // aria-describedby exposes it as the button's accessible description
      // instead (only when it's actually rendered below).
      aria-describedby={report.description ? descriptionId : undefined}
      onClick={() => onOpen(report)}
    >
      <div>
        <Text.BodyText3 bold tag="div">
          {report.title}
        </Text.BodyText3>
        {report.description && (
          // Text (uikit) doesn't forward an `id` prop, so the id that ties
          // this text to the button's aria-describedby lives on a wrapping
          // span instead.
          <span id={descriptionId}>
            <Text.BodyText5 color="grey70">{report.description}</Text.BodyText5>
          </span>
        )}
      </div>
      {/* Purely visual — the row itself is the single interactive control,
          so this must not be (or contain) another interactive element. */}
      <span className={`d-flex align-items-center ${css({ gap: '4px' })}`}>
        <Text.BodyText5 color="primary" tag="span">
          {t('view_report')}
        </Text.BodyText5>
        <ChevronRight fillColor={palette.belizeHole} />
      </span>
    </button>
  )
}

export default ReportButton
